import { sessionQuery, useApi, useAuthStore, WakingBanner } from '@classgo/app-kit';
import { AppShell, BrandLockup, Button, Card, Header, Menu, Sidebar, useToast } from '@classgo/ui';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import type { messages } from '../i18n';
import { useT } from '../i18n';

const NAV = [
  { href: '/', key: 'nav.tenants' },
  { href: '/contactos', key: 'nav.contacts' },
  { href: '/equipo', key: 'nav.team' },
] as const;

const TITLES: { prefix: string; key: keyof typeof messages }[] = [
  { prefix: '/colegios/nuevo', key: 'wizard.title' },
  { prefix: '/colegios/', key: 'tenants.title' },
  { prefix: '/contactos', key: 'contacts.title' },
  { prefix: '/equipo', key: 'team.title' },
  { prefix: '/', key: 'tenants.title' },
];

/**
 * A school user signed into the console by mistake: point them to the school app.
 */
const TenantNotice = ({ onLogout }: { onLogout: () => void }) => {
  const t = useT();
  return (
    <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <h1 className="text-lg font-semibold text-gray-900">{t('shell.tenantNotice.title')}</h1>
        <p className="mt-2 text-sm text-gray-600">{t('shell.tenantNotice.body')}</p>
        <Button className="mt-5" variant="secondary" onClick={onLogout}>
          {t('shell.logout')}
        </Button>
      </Card>
    </main>
  );
};

/**
 * The console's frame: lockup, two navigation entries, the staff member's menu. Only ClassGO
 * staff sessions get in; the API refuses everyone else on every `/staff/*` route anyway.
 */
export const AppLayout = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const auth = useAuthStore();
  const { data: session } = useSuspenseQuery(sessionQuery(api));
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [navigationOpen, setNavigationOpen] = useState(false);

  const logout = async () => {
    try {
      await api.request('/auth/logout', { method: 'POST' });
    } catch {
      // Revocation failed or the token was already gone: the local session ends regardless.
    }
    auth.signOutLocal('user');
    toast.show({ tone: 'info', title: t('shell.loggedOut') });
    void navigate({ to: '/login', search: { redirect: undefined } });
  };

  if (session.kind !== 'staff') {
    return <TenantNotice onLogout={() => void logout()} />;
  }
  const title = TITLES.find((entry) => pathname.startsWith(entry.prefix))?.key;
  const name = session.staff.givenName ?? session.staff.email;
  return (
    <AppShell
      navigationOpen={navigationOpen}
      onCloseNavigation={() => setNavigationOpen(false)}
      sidebar={
        <Sidebar
          brand={<BrandLockup height={26} />}
          sections={[
            {
              title: t('nav.main'),
              items: NAV.map((item) => ({ href: item.href, label: t(item.key) })),
            },
          ]}
          currentPath={pathname}
          renderLink={(item, className) => (
            <Link
              to={item.href as (typeof NAV)[number]['href']}
              className={className}
              onClick={() => setNavigationOpen(false)}
            >
              {item.label}
            </Link>
          )}
        />
      }
      header={
        <>
          <WakingBanner />
          <Header
            title={title ? t(title) : undefined}
            onOpenNavigation={() => setNavigationOpen(true)}
            actions={
              <Menu
                label={t('shell.userMenu')}
                trigger={<span className="text-sm font-medium text-gray-700">{name}</span>}
                items={[
                  {
                    id: 'logout',
                    label: t('shell.logout'),
                    onSelect: () => void logout(),
                    danger: true,
                  },
                ]}
              />
            }
          />
        </>
      }
    >
      <Outlet />
    </AppShell>
  );
};
